import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { MenuProvider, useMenuDispatcher, useMenuItemWithContext } from '../menuContext';
import { NavigationItemMenuModel } from './navigationItem/models/navigationItem.model';
import NavigationAccordionItem from './navigationAccordionItem';
import NavigationAccordionLinkItem from './navigationAccordionLinkItem';
import React, { useCallback } from 'react';
import { HeaderModel } from '../models/header.model';
import Icon from '../../@shared/icon';
import RoutableSitecoreLink from '../../@shared/routableSitecoreLink';
import Login from './login';
import { TSitecoreContext } from '../../../types/types';
import { documentService } from '../../../utils/window.utils';
import { googleClickCtaEvent } from '../../../utils/analytics';

const MobileMenu = ({
  model,
  fields,
  sitecoreContext,
  breakpointLg,
}: {
  model: HeaderModel;
  fields: ComponentFieldsGeneral;
  sitecoreContext: TSitecoreContext;
  breakpointLg: boolean;
}): JSX.Element => {
  // this is a custom NavigationItemMenuStructure
  const [isOpen, setOpen] = useMenuItemWithContext();
  const docElement = documentService();
  const handleClick = () => {
    setOpen(!isOpen);
    return !isOpen ? docElement?.body.classList.add('body--with-header-open') : docElement?.body.classList.remove('body--with-header-open');
  };
  const menuDispatcher = useMenuDispatcher();
  const closeMainNavigation = useCallback(() => {
    menuDispatcher({ type: 'closeAll' });
    docElement?.body?.classList.remove('body--with-header-open');
  }, [menuDispatcher, docElement]);
  const baseElementClassName = 'navigation__menu-item';
  const elementClassList = [baseElementClassName, baseElementClassName + '--mobile-menu'];
  if (isOpen) {
    elementClassList.push(baseElementClassName + '--open');
  }
  const elementClassName = elementClassList.join(' ');
  const icon = isOpen ? 'action-close' : 'set-nav-services-nav-menu';
  return (
    <li className={elementClassName}>
      <button className="navigation__menu-item__trigger" onClick={handleClick}>
        <span className="navigation__menu-item__trigger__content navigation__menu-item__trigger__content--with-icon">
          <Icon iconName={icon} extraClassName="navigation__menu-item__trigger__icon" size={22} />
          <span className="navigation__menu-item__trigger__text">
            <Text field={isOpen ? model.mobileTitleOpen : model.mobileTitle} />
          </span>
        </span>
      </button>
      <div className="navigation__menu-item__tray" style={{ display: isOpen ? 'block' : 'none' }}>
        {isOpen && (
          <div className="navigation__menu-mobile-wrapper">
            <MenuProvider>
              {/* menu provider for accordion, using related custom hooks in components inside here will relate to this */}
              <ul className="navigation__accordion-wrapper">
                {model.navigationItemModelsArr.map((navigationItemModel) =>
                  navigationItemModel instanceof NavigationItemMenuModel ? (
                    <NavigationAccordionItem
                      key={navigationItemModel?.id}
                      model={navigationItemModel}
                      closeMainNavigation={closeMainNavigation}
                    />
                  ) : (
                    <NavigationAccordionLinkItem
                      key={navigationItemModel?.id}
                      model={navigationItemModel}
                      closeMainNavigation={closeMainNavigation}
                    />
                  )
                )}
              </ul>
            </MenuProvider>

            {!breakpointLg && (
              <ul className="navigation__utility">
                {/* findADealer mobile*/}
                {fields?.findDealer && (
                  <li className="navigation__utility__item" onClick={handleClick}>
                    <RoutableSitecoreLink
                      field={fields?.findDealer?.link}
                      className="navigation__utility__link"
                      onClick={(): void =>
                        googleClickCtaEvent('Global Nav', fields?.findDealer?.title?.value, fields?.findDealer?.title?.value)
                      }
                    >
                      <Icon iconField={fields.findDealer?.icon} extraClassName="navigation__utility__icon" size={24} />
                      <RichText tag="span" className="navigation__utility__text rich-text" field={fields?.findDealer?.title} />
                    </RoutableSitecoreLink>
                  </li>
                )}
                {/* search mobile*/}
                {fields?.search && (
                  <li className="navigation__utility__item" onClick={handleClick}>
                    <RoutableSitecoreLink
                      field={fields?.search?.search}
                      className="navigation__utility__link"
                      onClick={(): void =>
                        googleClickCtaEvent('Global Nav', fields?.search?.search?.value?.text, fields?.search?.search?.value?.text)
                      }
                    >
                      <Icon iconField={fields.search?.icon} extraClassName="navigation__utility__icon" size={24} />
                      <span className="navigation__utility__text">{fields?.search?.search?.value?.text}</span>
                    </RoutableSitecoreLink>
                  </li>
                )}

                {/* Login */}
                {fields?.login && <Login fields={fields} view={'mobile'} sitecoreContext={sitecoreContext} />}
              </ul>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

export default MobileMenu;
